import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { 
  ArrowRight, 
  Smartphone, 
  Globe, 
  Workflow, 
  Brain,
  Code2,
  Terminal,
  ChevronRight,
  Zap,
  Shield,
  Users,
  CheckCircle2
} from "lucide-react";
import TestimonialsSection from "@/components/TestimonialsSection";
import FAQSection from "@/components/FAQSection";

const HomePage = () => {
  const { t } = useTranslation();
  const services = [
    {
      id: "mobile",
      icon: Smartphone,
      title: t('services.mobile.title'),
      subtitle: t('services.mobile.subtitle'),
      description: t('services.mobile.description'),
      features: t('services.mobile.features', { returnObjects: true }),
      color: "#00d4aa",
      technologies: ["React Native", "Flutter", "Swift", "Kotlin", "Firebase"]
    },
    {
      id: "web",
      icon: Globe,
      title: t('services.web.title'),
      subtitle: t('services.web.subtitle'),
      description: t('services.web.description'),
      features: t('services.web.features', { returnObjects: true }),
      color: "#0ea5e9",
      technologies: ["React", "Next.js", "Node.js", "Python", "PostgreSQL"]
    },
    {
      id: "n8n",
      icon: Workflow,
      title: t('services.n8n.title'),
      subtitle: t('services.n8n.subtitle'),
      description: t('services.n8n.description'),
      features: t('services.n8n.features', { returnObjects: true }),
      color: "#a855f7",
      technologies: ["n8n", "Zapier", "Make", "REST API", "Webhook"]
    },
    {
      id: "ai",
      icon: Brain,
      title: t('services.ai.title'),
      subtitle: t('services.ai.subtitle'),
      description: t('services.ai.description'),
      features: t('services.ai.features', { returnObjects: true }),
      color: "#f97316",
      technologies: ["Python", "TensorFlow", "OpenAI", "LangChain", "HuggingFace"]
    }
  ];

  const features = [
    {
      icon: Zap,
      title: t('whyUs.fastDev.title'),
      description: t('whyUs.fastDev.description')
    },
    {
      icon: Shield,
      title: t('whyUs.secureCoding.title'),
      description: t('whyUs.secureCoding.description')
    },
    {
      icon: Users,
      title: t('whyUs.support.title'),
      description: t('whyUs.support.description')
    }
  ];

  return (
    <div className="pt-16 md:pt-20" data-testid="home-page">
      {/* Hero Section */}
      <section className="hero-bg min-h-[90vh] flex items-center relative overflow-hidden" data-testid="hero-section">
        {/* Background decorations */}
        <div className="absolute inset-0 grid-pattern opacity-50" />
        <div className="absolute top-20 left-10 code-decoration hidden lg:block">
          {'const success = await buildProject();'}
        </div>
        <div className="absolute bottom-40 right-10 code-decoration hidden lg:block">
          {'// Code quality = Customer satisfaction'}
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#16161f] border border-[#2a2a3a] mb-6">
                <span className="w-2 h-2 rounded-full bg-[#00d4aa] animate-pulse" />
                <span className="text-sm text-[#9ca3af] font-mono">{t('hero.badge')}</span>
              </div>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#f0f0f5] leading-tight mb-6">
                {t('hero.title')}{" "}
                <span className="gradient-text">{t('hero.titleHighlight')}</span>
              </h1>
              
              <p className="text-lg text-[#9ca3af] mb-8 leading-relaxed max-w-xl">
                {t('hero.description')}
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Link 
                  to="/iletisim" 
                  className="btn-primary flex items-center justify-center gap-2"
                  data-testid="hero-cta-primary"
                >
                  <Terminal className="w-5 h-5" />
                  {t('hero.startProject')}
                  <ArrowRight className="w-4 h-4" />
                </Link>
                <Link 
                  to="/urunler" 
                  className="btn-secondary flex items-center justify-center gap-2"
                  data-testid="hero-cta-secondary"
                >
                  {t('hero.viewProducts')}
                  <ChevronRight className="w-4 h-4" />
                </Link>
              </div>
              
              {/* Stats */}
              <div className="grid grid-cols-3 gap-6 mt-12 pt-8 border-t border-[#2a2a3a]">
                <div>
                  <div className="stat-number text-2xl sm:text-3xl">50+</div>
                  <p className="text-sm text-[#9ca3af] mt-1">{t('hero.completedProjects')}</p>
                </div>
                <div>
                  <div className="stat-number text-2xl sm:text-3xl">30+</div>
                  <p className="text-sm text-[#9ca3af] mt-1">{t('hero.happyClients')}</p>
                </div>
                <div>
                  <div className="stat-number text-2xl sm:text-3xl">5+</div>
                  <p className="text-sm text-[#9ca3af] mt-1">{t('hero.yearsExperience')}</p>
                </div>
              </div>
            </div>
            
            {/* Code Block Decoration */}
            <div className="hidden lg:block">
              <div className="code-block relative">
                <div className="flex items-center gap-2 mb-4 pb-4 border-b border-[#2a2a3a]">
                  <div className="w-3 h-3 rounded-full bg-[#ff5f56]" />
                  <div className="w-3 h-3 rounded-full bg-[#ffbd2e]" />
                  <div className="w-3 h-3 rounded-full bg-[#27c93f]" />
                  <span className="ml-4 text-[#9ca3af] text-sm">project.js</span>
                </div>
                <pre className="text-sm leading-relaxed">
                  <code>
                    <span className="text-[#a855f7]">const</span>{" "}
                    <span className="text-[#f0f0f5]">bfCodeLabs</span>{" "}
                    <span className="text-[#9ca3af]">=</span>{" "}
                    <span className="text-[#0ea5e9]">{"{"}</span>
                    {"\n"}
                    {"  "}<span className="text-[#00d4aa]">services</span>
                    <span className="text-[#9ca3af]">:</span>{" "}
                    <span className="text-[#0ea5e9]">[</span>
                    {"\n"}
                    {"    "}<span className="text-[#f97316]">"Mobil Uygulama"</span>,
                    {"\n"}
                    {"    "}<span className="text-[#f97316]">"Web Geliştirme"</span>,
                    {"\n"}
                    {"    "}<span className="text-[#f97316]">"n8n Otomasyon"</span>,
                    {"\n"}
                    {"    "}<span className="text-[#f97316]">"Yapay Zeka"</span>
                    {"\n"}
                    {"  "}<span className="text-[#0ea5e9]">]</span>,
                    {"\n"}
                    {"  "}<span className="text-[#00d4aa]">quality</span>
                    <span className="text-[#9ca3af]">:</span>{" "}
                    <span className="text-[#a855f7]">true</span>,
                    {"\n"}
                    {"  "}<span className="text-[#00d4aa]">innovation</span>
                    <span className="text-[#9ca3af]">:</span>{" "}
                    <span className="text-[#f97316]">"∞"</span>
                    {"\n"}
                    <span className="text-[#0ea5e9]">{"}"}</span>;
                    {"\n\n"}
                    <span className="text-[#a855f7]">await</span>{" "}
                    <span className="text-[#f0f0f5]">bfCodeLabs</span>.
                    <span className="text-[#00d4aa]">buildYourDream</span>
                    <span className="text-[#0ea5e9]">()</span>;
                    <span className="terminal-cursor ml-1" />
                  </code>
                </pre>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Detail */}
      <section className="py-20 bg-[#0a0a0f]" data-testid="services-preview">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-[#00d4aa] font-mono text-sm mb-4 block">
              {t('services.subtitle')}
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#f0f0f5] mb-4">
              {t('services.title')}
            </h2>
            <p className="text-[#9ca3af] max-w-2xl mx-auto">
              {t('services.description')}
            </p>
          </div>

          <div className="divide-y divide-[#2a2a3a]">
            {services.map((service, index) => (
              <div 
                key={service.id}
                className={`grid lg:grid-cols-2 gap-12 items-center py-12 md:py-16 ${
                  index % 2 === 1 ? 'lg:grid-flow-dense' : ''
                }`}
                data-testid={`service-detail-${service.id}`}
              >
                <div className={index % 2 === 1 ? 'lg:col-start-2' : ''}>
                  <div 
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full border mb-6"
                    style={{ 
                      borderColor: service.color,
                      backgroundColor: `${service.color}10`
                    }}
                  >
                    <service.icon 
                      className="w-5 h-5" 
                      style={{ color: service.color }} 
                    />
                    <span 
                      className="text-sm font-medium"
                      style={{ color: service.color }}
                    >
                      {service.subtitle}
                    </span>
                  </div>
                  
                  <h3 className="text-2xl sm:text-3xl font-bold text-[#f0f0f5] mb-4">
                    {service.title}
                  </h3>
                  
                  <p className="text-[#9ca3af] mb-6 leading-relaxed">
                    {service.description}
                  </p>

                  <div className="grid sm:grid-cols-2 gap-3 mb-6">
                    {service.features.map((feature, fIndex) => (
                      <div 
                        key={fIndex}
                        className="flex items-center gap-2"
                      >
                        <CheckCircle2 
                          className="w-5 h-5 flex-shrink-0" 
                          style={{ color: service.color }}
                        />
                        <span className="text-[#f0f0f5] text-sm">{feature}</span>
                      </div>
                    ))}
                  </div>

                  <div className="flex flex-wrap gap-2">
                    {service.technologies.map((tech, tIndex) => (
                      <span 
                        key={tIndex}
                        className="tech-badge"
                      >
                        {tech}
                      </span>
                    ))}
                  </div>
                </div>

                <div className={index % 2 === 1 ? 'lg:col-start-1' : ''}>
                  <div 
                    className="bg-[#16161f] border border-[#2a2a3a] rounded-xl p-8 relative overflow-hidden"
                  >
                    <div 
                      className="absolute top-0 right-0 w-40 h-40 rounded-full blur-3xl opacity-20"
                      style={{ backgroundColor: service.color }}
                    />
                    
                    <div className="relative z-10">
                      <div 
                        className="w-20 h-20 rounded-xl flex items-center justify-center mb-6"
                        style={{ 
                          background: `linear-gradient(135deg, ${service.color}20, ${service.color}10)`,
                          border: `1px solid ${service.color}30`
                        }}
                      >
                        <service.icon 
                          className="w-10 h-10" 
                          style={{ color: service.color }}
                        />
                      </div>

                      <div className="code-block">
                        <div className="flex items-center gap-2 mb-3 pb-3 border-b border-[#2a2a3a]">
                          <div className="w-2.5 h-2.5 rounded-full bg-[#ff5f56]" />
                          <div className="w-2.5 h-2.5 rounded-full bg-[#ffbd2e]" />
                          <div className="w-2.5 h-2.5 rounded-full bg-[#27c93f]" />
                          <span className="ml-3 text-[#9ca3af] text-xs">{service.id}.config</span>
                        </div>
                        <pre className="text-xs leading-relaxed">
                          <code>
                            <span className="text-[#a855f7]">export const</span>{" "}
                            <span className="text-[#f0f0f5]">config</span>{" "}
                            <span className="text-[#9ca3af]">=</span>{" "}
                            <span className="text-[#0ea5e9]">{"{"}</span>
                            {"\n"}
                            {"  "}<span style={{ color: service.color }}>service</span>
                            <span className="text-[#9ca3af]">:</span>{" "}
                            <span className="text-[#f97316]">"{service.title}"</span>,
                            {"\n"}
                            {"  "}<span style={{ color: service.color }}>quality</span>
                            <span className="text-[#9ca3af]">:</span>{" "}
                            <span className="text-[#a855f7]">"premium"</span>,
                            {"\n"}
                            {"  "}<span style={{ color: service.color }}>support</span>
                            <span className="text-[#9ca3af]">:</span>{" "}
                            <span className="text-[#a855f7]">true</span>
                            {"\n"}
                            <span className="text-[#0ea5e9]">{"}"}</span>;
                          </code>
                        </pre>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-16">
            <Link
              to="/iletisim"
              className="btn-primary inline-flex items-center gap-2"
              data-testid="services-cta-btn"
            >
              <Terminal className="w-5 h-5" />
              {t('services.getQuote')}
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 bg-[#12121a]" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-[#00d4aa] font-mono text-sm mb-4 block">
                {t('whyUs.subtitle')}
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold text-[#f0f0f5] mb-6">
                {t('whyUs.title')}{" "}
                <span className="gradient-text">{t('whyUs.titleHighlight')}</span>
              </h2>
              <p className="text-[#9ca3af] mb-8 leading-relaxed">
                {t('whyUs.description')}
              </p>

              <div className="space-y-6">
                {features.map((feature, index) => (
                  <div key={index} className="flex gap-4">
                    <div className="w-12 h-12 rounded-lg bg-[#16161f] border border-[#2a2a3a] flex items-center justify-center flex-shrink-0">
                      <feature.icon className="w-6 h-6 text-[#00d4aa]" />
                    </div>
                    <div>
                      <h3 className="text-[#f0f0f5] font-semibold mb-1">
                        {feature.title}
                      </h3>
                      <p className="text-[#9ca3af] text-sm">
                        {feature.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative">
              <div className="bg-[#16161f] border border-[#2a2a3a] rounded-xl p-8">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#00d4aa] to-[#0ea5e9] flex items-center justify-center">
                    <Code2 className="w-8 h-8 text-[#0a0a0f]" />
                  </div>
                  <div>
                    <h3 className="text-[#f0f0f5] font-semibold">BF Code Labs</h3>
                    <p className="text-[#9ca3af] text-sm">Yazılım Çözümleri</p>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-[#9ca3af] text-sm">{t('whyUs.satisfaction')}</span>
                    <span className="text-[#00d4aa] font-mono font-semibold">98%</span>
                  </div>
                  <div className="h-2 bg-[#2a2a3a] rounded-full overflow-hidden">
                    <div className="h-full w-[98%] bg-gradient-to-r from-[#00d4aa] to-[#0ea5e9] rounded-full" />
                  </div>
                  
                  <div className="flex justify-between items-center mt-4">
                    <span className="text-[#9ca3af] text-sm">{t('whyUs.onTimeDelivery')}</span>
                    <span className="text-[#00d4aa] font-mono font-semibold">95%</span>
                  </div>
                  <div className="h-2 bg-[#2a2a3a] rounded-full overflow-hidden">
                    <div className="h-full w-[95%] bg-gradient-to-r from-[#00d4aa] to-[#0ea5e9] rounded-full" />
                  </div>
                </div>
              </div>
              
              {/* Decorative elements */}
              <div className="absolute -top-4 -right-4 w-24 h-24 bg-[#00d4aa]/10 rounded-full blur-2xl" />
              <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-[#0ea5e9]/10 rounded-full blur-2xl" />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#0a0a0f] relative overflow-hidden" data-testid="cta-section">
        <div className="absolute inset-0 grid-pattern opacity-30" />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <span className="text-[#00d4aa] font-mono text-sm mb-4 block">
            {t('cta.subtitle')}
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold text-[#f0f0f5] mb-6">
            {t('cta.title')}{" "}
            <span className="gradient-text">{t('cta.titleHighlight')}</span>
          </h2>
          <p className="text-[#9ca3af] mb-8 max-w-2xl mx-auto">
            {t('cta.description')}
          </p>
          <Link
            to="/iletisim"
            className="btn-primary inline-flex items-center gap-2 text-lg px-8 py-4"
            data-testid="cta-contact-btn"
          >
            <Terminal className="w-5 h-5" />
            {t('cta.getQuote')}
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>

      {/* Testimonials */}
      <TestimonialsSection />

      {/* FAQ */}
      <FAQSection />
    </div>
  );
};

export default HomePage;
